/**
 * Types shared by the defect assistant's API calls, store slice and components.
 */

export type DefectAssistantSuggestion = {
  // Defect code prefix, e.g. "GECB"
  defectCode: string
  // Path before the label, segments joined with " > " (may be empty)
  category: string
  // Human-readable label for the prefix, e.g. "STM-keskusyksikkö"
  label: string
  // Empty from /comment: the "why to choose" defectCodeAdvice streams in and is fetched per prefix
  // afterwards via fetchDefectCodeAdvice (keyed by the response's generationId).
  description?: string
}

export type DefectAssistantImage = {
  base64: string
  mimeType: string
}

export type DefectAssistantSuggestionsResponse = {
  suggestions: DefectAssistantSuggestion[]
  // Backend-driven flow-control options ("none of these — refine/give up") shown after suggestions
  metaOptions: OptionValue[]
  // Identifies the in-flight suggestion stream so each prefix's defectCodeAdvice can be pulled separately.
  generationId: string | null
  // The steps the assistant expects to ask once a suggestion is picked.
  futureQuestions: FutureQuestion[]
}

/**
 * An upcoming step the assistant plans to ask after the questions it has already returned. Rendered
 * as a dimmed placeholder so the page has its shape before the flow gets there.
 *
 * The plan is best-effort and server-owned: it may gain, lose or reorder steps between turns, and
 * empties out as the report finalizes.
 */
export type FutureQuestion = {
  // Same wire string as QnA.questionType, so a hint keeps its identity across turns.
  type: string
  // Ready-to-render title from the backend — a display string, not a translation key.
  label: string
}

export type DefectAssistantDefectCodeAdvice = {
  prefix: string
  defectCodeAdvice: string | null
  // false ⇒ not available (timeout / expired / none produced); render the localized fallback.
  ready: boolean
}

export const OptionKinds = {
  ANSWER: 'ANSWER',
  DISCARD: 'DISCARD',
  DISCARD_AND_REFINE: 'DISCARD_AND_REFINE',
  GIVE_UP: 'GIVE_UP',
  FREE_TEXT: 'FREE_TEXT',
  NOTHING_TO_ADD: 'NOTHING_TO_ADD',
  ADD_COMMENT_TO_EXISTING_DEFECT: 'ADD_COMMENT_TO_EXISTING_DEFECT',
  CREATE_NEW_DEFECT: 'CREATE_NEW_DEFECT',
  // Palvelurajoite confirmation. Picking ADD_ puts the option's value on body.restrictionCode.
  ADD_LIMITATION_OF_USAGE: 'ADD_LIMITATION_OF_USAGE',
  DECLINE_LIMITATION_OF_USAGE: 'DECLINE_LIMITATION_OF_USAGE',
} as const

export type OptionKind = (typeof OptionKinds)[keyof typeof OptionKinds]

export const AnswerTypes = {
  SINGLE: 'SINGLE',
  MULTISELECT: 'MULTISELECT',
} as const

export type AnswerType = (typeof AnswerTypes)[keyof typeof AnswerTypes]

export type OptionValue = {
  label: string
  value: string | number
  additionalInfo?: string | null
  kind: OptionKind
}

export type MatchedDefect = {
  id: string
  defectCode: string
  defectType: string
  description: string | null
  comments: string | null
  issuedTime: string | null
  status: string
  hasNewInformation: boolean
  // The proposed comment, inlined. Null when the backend streams it instead — see suggestedCommentId
  // — and when the match adds nothing worth appending.
  suggestedComment: string | null
  // Handle for fetching that comment separately, while the worker is still reading the match. At most
  // one of the two is ever set.
  suggestedCommentId?: string | null
}

export type QnA = {
  uuid: string
  questionType: string
  question: string
  options: OptionValue[]
  answerType: AnswerType
  answers?: OptionValue[] | null
  summary?: string | null
  matchedDefect?: MatchedDefect | null
}

export type ReportDefectBody = {
  userComment: string
  aiComment: string
  defectCode: string
  defectType: string
  installationGroup: string
  restrictionCode?: string
  vehicleSeries: string
  // null when position not chosen, empty when resolved to no position
  vehiclePosition: string | null
  // undefined: backend omitted the field; null: backend explicitly cleared a resolved item id.
  itemId?: string | null
}

export type ReportDefectQuestion = {
  body: ReportDefectBody
  questionAnswers: QnA[]
  futureQuestions?: FutureQuestion[]
  // Handle for polling the streamed description. Set only on the turn the report finalizes, and then
  // body.aiComment stays empty on purpose — the text arrives through fetchSummarySnapshot.
  finalizationId?: string | null
}

export type DuplicateCommentResult = {
  suggestedComment: string | null
  // false ⇒ unavailable (unknown handle / timeout / nothing to suggest); the worker writes their own.
  ready: boolean
}

export const FinalizationStates = {
  /** More text is still coming; poll again. */
  STREAMING: 'STREAMING',
  /** The description is whole. Stop polling. */
  DONE: 'DONE',
  /** The stream broke. Fall back to the blocking description. */
  FAILED: 'FAILED',
  /** Unknown or aged-out handle. Fall back to the blocking description. */
  EXPIRED: 'EXPIRED',
} as const

export type FinalizationState = (typeof FinalizationStates)[keyof typeof FinalizationStates]

export type FinalizationSnapshot = {
  // Everything accumulated so far, never a delta — a missed poll loses nothing.
  summary: string | null
  state: FinalizationState
}

export type DefectAssistantValidateRequest = {
  description: string
  defectCode: string
  defectGroup?: string
  vehicleType: string
  serialNumber: string
}

export type DefectAssistantValidateResponseSeverity = 'ok' | 'warning' | 'error'

export type DefectAssistantValidateResponse = {
  severity: DefectAssistantValidateResponseSeverity
  reasoning: string
  suggestedFixes: string[]
}

/** Mutually exclusive position in the assistant workflow. */
export type FlowStep =
  | { step: 'idle' }
  | { step: 'analyzing' }
  | { step: 'choosing' }
  | { step: 'detailing' }
  | { step: 'questioning'; loading: boolean }
  | { step: 'failed'; source: 'analyze' | 'questions' }

/** Text fetched separately from the response it belongs to, e.g. defect code advice. */
export interface FetchedText {
  status: 'loading' | 'ready' | 'failed'
  text: string
}

/**
 * A suggestion generation retained as part of the scrollable history.
 * `detail` is `null` until submitted; an empty string records submission without extra detail.
 */
export interface SuggestionRound {
  generationId: string | null
  suggestions: DefectAssistantSuggestion[]
  metaOptions: OptionValue[]
  detail: string | null
  excluded: boolean
}

export type SummaryStatus = 'idle' | 'streaming' | 'done' | 'failed'

/**
 * The AI-written defect description, wherever it came from. The backend streams it for us: the
 * finalizing turn hands back a `finalizationId` and leaves `body.aiComment` empty, and the text is
 * polled in. `text` is therefore the single source of truth for the description — a backend that
 * answered inline on `body.aiComment` lands here as an immediately-`done` summary.
 */
export interface SummaryState {
  /** Handle being polled, or `null` when the description did not come from a stream. */
  finalizationId: string | null
  /** Everything received so far. Snapshots are cumulative, so this is never a partial delta. */
  text: string
  status: SummaryStatus
  /** Whether the blocking re-run has already been tried, so a broken stream can't loop. */
  fallbackAttempted: boolean
  /**
   * Polls that errored since the last good one. Snapshots are cumulative and idempotent, so a single
   * failed poll costs nothing and is worth retrying; a run of them means the endpoint is not there.
   */
  failedPolls: number
}
